<template>
  <v-app>
    <v-app-bar 
      app 
      flat 
      color="white" 
      height="60px" 
      class='pt-2 mb-6' 
      :extension-height="form.status ? '30px' : '0px'"
      style="position: sticky; top: 0; z-index: 1000;"
    >
      <!-- ヘッダー（1行目）：日付切り替え -->
      <div style="width: 100%;" @click="openDateDialog">
        <DatePager :date="date" @addDay="addDay" @subtractDay="subtractDay" />
      </div>

      <!-- ヘッダー（2行目）：extension スロット -->
      <template v-slot:extension class='pb-4'>
        <v-container fluid class="pa-0">
          <div class="d-flex align-center justify-space-between px-2 fill-height">
            <!-- 左端のステータスチップ -->
            <div>
              <v-chip
                size="small"
                variant="outlined"
                color="gray"
                class="mb-4"
                v-if="form.status === 'draft'"
              >
                {{ $t('diary.draft') }}
              </v-chip>
              <v-chip
                size="small"
                variant="outlined"
                color="secondary"
                class="mb-4"
                v-if="form.status === 'applied'"
              >
                {{ $t('diary.applied') }}
              </v-chip>
              <v-chip
                size="small"
                variant="outlined"
                color="primary"
                class="mb-4"
                v-if="form.status === 'approved'"
              >
                {{ $t('diary.approved') }}
              </v-chip>
            </div>

            <v-spacer />

            <!-- 右端の承認者のバッジ -->
            <div>
              <v-chip
                size="small"
                color="primary"
                class="mb-4 mr-2 text-white"
                v-if="form.status === 'approved' && approverRoles.chief"
              >
                <v-icon size="small" class="mr-1">mdi-check-circle</v-icon>
                {{ $t('diary.chief') }}
              </v-chip>
            </div>
            <div>
              <v-chip
                size="small"
                color="primary"
                class="mb-4 text-white"
                v-if="form.status === 'approved' && approverRoles.director"
              >
                <v-icon size="small" class="mr-1">mdi-check-circle</v-icon>
                {{ $t('diary.director') }}
              </v-chip>
            </div>
          </div>
        </v-container>
      </template>
    </v-app-bar>

    <div v-if="isLoading">
      <!-- ローダー -->
      <VueLoading :height="'50vh'" />
    </div>
    <div v-else>
      <!-- メインコンテンツ -->
      <v-main class='pa-1'>
        <v-container fluid class="py-4">
          <!-- 自由記録 -->
          <v-textarea
            v-model="form.freeRecord"
            :label="$t('diary.freeRecord')"
            variant="outlined"
            auto-grow
            class="mb-2"
          />

          <!-- 園行事 / 来訪者 -->
          <v-textarea
            v-model="form.event"
            :label="$t('diary.event')"
            variant="outlined"
            auto-grow
            class="mb-2"
          />

          <!-- 早退者 (理由) -->
          <v-textarea
            v-model="form.earlyLeaver"
            :label="$t('diary.earlyLeaver')"
            variant="outlined"
            auto-grow
            class="mb-2"
          />

          <!-- 申請者（一度決定されたら変更不可） -->
          <v-select
            v-model="form.appliedBy"
            :items="staffList"
            item-title="text"
            item-value="value"
            :label="$t('diary.applicant')"
            variant="outlined"
            class="mb-2"
          />

          <!-- 承認者 (主任) -->
          <v-select
            v-model="form.approvedByChief"
            :items="staffList"
            item-title="text"
            item-value="value"
            :label="$t('diary.approverChief')"
            variant="outlined"
            :disabled="true"
            class="mb-2"
            v-if='form.status === "applied" || form.status === "approved"'
          />

          <!-- 承認者 (施設長) -->
          <v-select
            v-model="form.approvedByDirector"
            :items="staffList"
            item-title="text"
            item-value="value"
            :label="$t('diary.approverDirector')"
            variant="outlined"
            :disabled="true"
            class="mb-2"
            v-if='form.status === "applied" || form.status === "approved"'
          />

          <!-- ボタン群 -->
          <v-row dense>
            <v-col cols="6" v-if='!form.status || form.status === "draft"'>
              <v-btn block variant="outlined" @click="saveDraft">{{ $t('diary.saveDraft') }}</v-btn>
            </v-col>
            <v-col cols="6" v-if='!form.status || form.status === "draft"'>
              <v-btn block color="primary" variant="flat" @click="saveAndApply">{{ $t('diary.saveAndApply') }}</v-btn>
            </v-col>

            <!-- 承認ボタン -->
            <v-col
              cols="6"
              v-if='(form.status === "applied" || form.status === "approved") && currentSchool && (currentSchool.isChief || currentSchool.isDirector)'
            >
              <v-btn block variant="outlined" color="error" @click="changeApprovalStatus('rejected')">{{ $t('diary.reject') }}</v-btn>
            </v-col>
            <v-col
              cols="6"
              v-if='(form.status === "applied" || form.status === "approved") && currentSchool && (currentSchool.isChief || currentSchool.isDirector)'
            >
              <v-btn block color="primary" variant="flat" :disabled='isApproveDisabled' @click="changeApprovalStatus('approved')">{{ $t('diary.approve') }}</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-main>
    </div>
    <v-dialog
      v-model="dateDialogFlg"
      width="auto"
      max-width="100%"
    >
      <v-card>
        <v-date-picker
          v-model="selectedDate"
          type="date"
          :day-format="(date) => new Date(date).getDate()"
          @update:model-value="dateSelected"
          no-title
          hide-header
          color="primary"
        >
        </v-date-picker>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script lang="ts">
//import Vue from 'vue';
import { defineComponent } from "vue";
import dayjs from 'dayjs';
import DatePager from '@/components/molecules/DatePager.vue';
import VueLoading from '@/components/pages/Loding.vue';
import { schoolDiaryModule } from '@/store/dataModules/schoolDiaryModule';
import {
  ApproveSchoolDiaryRequest,
  ChangeSchoolDiaryRequest,
  GetSchoolDiaryRequest,
  PostSchoolDiaryRequest,
} from '@/model/Request';
import { MeNurserySchoolResponse, MeResponse } from 'chaild-api/lib';
import LocalDataService from '@/service/LocalDataService';
import { nurseryModule } from '@/store/dataModules/nurseryModule';
import Swal from 'sweetalert2';

export default defineComponent({
  name: 'NurserySchoolDiaryContainer',
  components: { DatePager, VueLoading },
  data() {
    return {
      dateDialogFlg: false,
      selectedDate: dayjs().format('YYYY-MM-DD'),
      addflg: false,
      subtractflg: false,
      isLoading: true,
      date: dayjs(),
      form: {
        date: dayjs().format('YYYY-MM-DD'),
        freeRecord: null as string | null,
        event: null as string | null,
        earlyLeaver: null as string | null,
        appliedBy: null as number | null,
        approvedByChief: null as number | null,
        approvedByDirector: null as number | null,
        status: 'draft' as string | null,
      },
      staffList: [] as { text: string; value: number }[],
      approverRoles: {
        chief: false,
        director: false,
      },
    };
  },
  computed: {
    me(): MeResponse | null {
      return LocalDataService.getUser();
    },
    currentSchool(): MeNurserySchoolResponse | undefined {
      if (this.me) {
        let nurserySchool = this.me.nurserySchools.find(nursery => nursery.isDefault);
        if (this.me.nurserySchools.length === 0) {
          return undefined
        }
        if (!nurserySchool) {
          nurserySchool = this.me.nurserySchools[0];
        }

        return nurserySchool;
      }
      return undefined;
    },
    allStaff(): {userId: number; fullName: string;}[] {
      if (nurseryModule.allUser) {
        let allStaff = [] as any;
        nurseryModule.allUser.users.map(user => {
          allStaff.push({
            userId: user.userId,
            fullName: user.lastName + user.firstName,
          });
        });
        return allStaff;
      }
      return [];
    },
    isApproveDisabled(): boolean {
      if (this.form.status !== 'approved') return false;
      const meId = this.currentSchool?.userId;
      const isChief = this.currentSchool?.isChief;
      const isDirector = this.currentSchool?.isDirector;


      if (isChief && isDirector) {
        console.log(this.form.approvedByChief, this.form.approvedByDirector, meId);
        return this.form.approvedByChief === meId && this.form.approvedByDirector === meId;
      } else if (isChief) {
        return this.form.approvedByChief === meId;
      } else if (isDirector) {
        return this.form.approvedByDirector === meId;
      }

      return false;
    }
  },
  watch: {
    date: {
      immediate: true,
      handler() {
        this.loadDetail();
      }
    }
  },
  methods: {
    addDay() {
      this.date = this.date.add(1, 'day');
      sessionStorage.setItem('nurserySchoolDairyDate', this.date.format('YYYY-MM-DD'));
      this.addflg = true;
    },
    subtractDay() {
      this.date = this.date.subtract(1, 'day');
      sessionStorage.setItem('nurserySchoolDairyDate', this.date.format('YYYY-MM-DD'));
      this.subtractflg = true;
    },
    openDateDialog() {
      if (this.addflg || this.subtractflg) {
        this.addflg = false;
        this.subtractflg = false;
        return
      }
      this.selectedDate = this.date.format('YYYY-MM-DD');
      this.dateDialogFlg = true;
    },
    dateSelected(day) {
      this.date = dayjs(day);
      this.selectedDate = dayjs(day).format('YYYY-MM-DD');
      sessionStorage.setItem('nurserySchoolDairyDate', this.date.format('YYYY-MM-DD'));
      this.dateDialogFlg = false;
    },
    loadDetail: async function() {
      this.isLoading = true;

      this.form.status = null;
      this.form.date = this.date.format('YYYY-MM-DD');
      this.form.freeRecord = null;
      this.form.event = null;
      this.form.earlyLeaver = null;
      this.form.approvedByChief = null;
      this.form.approvedByDirector = null;
      this.form.appliedBy = null;
      this.approverRoles.chief = false;
      this.approverRoles.director = false;

      try {
        await nurseryModule.getNurseryUser({
          nurseryId: this.currentSchool?.nurseryId ?? -1,
          date: this.form.date,
        });
        this.staffList = this.allStaff.map(u => ({
          text: u.fullName,
          value: u.userId,
        }));

        const req: GetSchoolDiaryRequest = {
          nurseryId: this.currentSchool?.nurseryId ?? -1,
          date: this.form.date,
        };
        const d = await schoolDiaryModule.getSchoolDiary(req);
        const chief = d.approvedBy?.chief === -1 ? undefined : d.approvedBy?.chief;
        const director = d.approvedBy?.director === -1 ? undefined : d.approvedBy?.director;

        this.approverRoles.chief = !!chief;
        this.approverRoles.director = !!director;

        // NOTE: 承認してもAPIのstatus="applied"は変わらないので、ここで調整する
        const status = (chief || director) ? 'approved' : d.status;

        Object.assign(this.form, {
          freeRecord: d.comment,
          event: d.event,
          earlyLeaver: d.earlyLeaver,
          appliedBy: d.createdBy?.userNurseryId,
          approvedByChief: chief?.userNurseryId || null,
          approvedByDirector: director?.userNurseryId || null,
          status,
        });
      } finally {
        this.isLoading = false;
      }
    },
    async saveDraft() {
      this.isLoading = true;
      this.form.status = null;
      try {
        if (schoolDiaryModule.diaryDetail?.schoolDiaryId) {
          const req: ChangeSchoolDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            diaryId: schoolDiaryModule.diaryDetail.schoolDiaryId,
            comment: this.form.freeRecord ?? undefined,
            earlyLeaver: this.form.earlyLeaver ?? undefined,
            event: this.form.event ?? undefined,
            status: 'draft',
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await schoolDiaryModule.updateSchoolDiary(req);
        } else {
          const req: PostSchoolDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            date: this.form.date,
            comment: this.form.freeRecord ?? undefined,
            earlyLeaver: this.form.earlyLeaver ?? undefined,
            event: this.form.event ?? undefined,
            status: 'draft',
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await schoolDiaryModule.postSchoolDiary(req);
        }
        await this.loadDetail();
      } finally {
        this.isLoading = false;
      }
    },
    async saveAndApply() {
      this.isLoading = true;
      this.form.status = null;
      try {
        if (schoolDiaryModule.diaryDetail?.schoolDiaryId) {
          const req: ChangeSchoolDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            diaryId: schoolDiaryModule.diaryDetail.schoolDiaryId,
            comment: this.form.freeRecord ?? undefined,
            earlyLeaver: this.form.earlyLeaver ?? undefined,
            event: this.form.event ?? undefined,
            status: 'applied',
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await schoolDiaryModule.updateSchoolDiary(req);
        } else {
          const req: PostSchoolDiaryRequest = {
            nurseryId: this.currentSchool?.nurseryId ?? -1,
            date: this.form.date,
            comment: this.form.freeRecord ?? undefined,
            earlyLeaver: this.form.earlyLeaver ?? undefined,
            event: this.form.event ?? undefined,
            status: 'applied',
            appliedBy: this.form.appliedBy ?? this.currentSchool?.userId,
          };
          await schoolDiaryModule.postSchoolDiary(req);
        }
        await this.loadDetail();
      } finally {
        this.isLoading = false;
      }
    },
    async changeApprovalStatus(approvalStatus: 'approved' | 'rejected') {
      // If rejecting an approved diary, show confirmation dialog
      if (approvalStatus === 'rejected' && this.form.status === 'approved') {
        const result = await Swal.fire({
          title: (this as any).$t('diary.confirmReject'),
          html: (this as any).$t('diary.willBeRejected'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: (this as any).$t('diary.reject'),
          cancelButtonText: (this as any).$t('diary.cancel'),
        });
        if (!result.isConfirmed) {
          return;
        }
      }

      this.isLoading = true;
      this.form.status = null;
      try {
        const req: ApproveSchoolDiaryRequest = {
          nurseryId: this.currentSchool?.nurseryId ?? -1,
          diaryId: schoolDiaryModule.diaryDetail?.schoolDiaryId ?? -1,
          approvalStatus,
        };
        await schoolDiaryModule.approveSchoolDiary(req);
        await this.loadDetail();
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    const nurserySchoolDairyDate = sessionStorage.getItem('nurserySchoolDairyDate');
    if (nurserySchoolDairyDate) this.date = dayjs(nurserySchoolDairyDate);
  }
});
</script>

<style scoped>
</style>
